import type { PropsOf } from '@emotion/react';

export type ButtonOrLinkElement = 'a' | 'button';

interface ButtonOrLink<T extends ButtonOrLinkElement> {
  /**
   * Should the element render an anchor or a button
   */
  as?: T;
  /**
   * Whether the link is to an external site. Only applies when `as="a"`
   */
  isExternal?: T extends 'a' ? boolean : undefined;
  /**
   * Whether the button rendered is disabled. Only applies when `as="button"`
   */
  isDisabled?: T extends 'button' ? boolean : undefined;
}

export interface BaseLinkProps<T extends ButtonOrLinkElement>
  extends ButtonOrLink<T>,
    Omit<PropsOf<'a'>, 'target' | 'children'> {}

export interface BaseButtonProps<T extends ButtonOrLinkElement>
  extends ButtonOrLink<T>,
    Omit<PropsOf<'button'>, 'disabled' | 'children'> {}

export type ButtonOrLinkProps<T extends ButtonOrLinkElement> = T extends 'a'
  ? BaseLinkProps<T>
  : BaseButtonProps<T>;

/**
 * Constant for noopener rel value
 */
const NO_OPENER = 'noopener' as const;
/**
 * Helper to compute rel attribute when link is external
 * @param rel the rel value from component props
 */
export const externalRel = (rel: string | undefined): string => {
  if (!rel) {
    return NO_OPENER;
  }

  if (rel.includes(NO_OPENER)) {
    return rel;
  }

  return `${rel} ${NO_OPENER}`;
};
